import {SortingState} from '@tanstack/table-core';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {MRT_ColumnDef, MRT_Row, MRT_RowData} from 'material-react-table';
import React, {useState, useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import StarIcon from '@mui/icons-material/Star';
import {Box, CircularProgress, Divider, Stack, Tooltip} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import getCreatorProfilePageUrl from '@/utils/getCreatorProfilePageUrl';
import translate from '@/utils/translate';
import {
  SocialMediaPost,
  SocialStream,
  SocialStreamTab,
} from '@/types/socialMediaPost';
import EmptyMetric, {
  EmptyDash,
} from '@/views/campaigns/stats/components/empty-metric';
import {
  formatDuration,
  formatNumber,
  formatPercentage,
  formatCurrency,
} from '@/views/campaigns/stats/utils';
import ColumnSelector, {ColumnDefinition} from '@/components/column-selector';
import {PROFILE_SOURCE} from '@/components/creator-profile/config/profileSources';
import {PROFILE_SOURCE_QUERY_PARAM} from '@/components/creator-profile/hooks/use-profile-source';
import Link from '@/components/link';
import ProfilePopup from '@/components/profile/ProfilePopup';
import SearchInput from '@/components/search-input';
import Table from '@/components/table';
import useCreatorProfileQuery from '@/hooks/queries/use-creator-profile-query';
import useBrandId from '@/hooks/use-brand-id';
import layoutStyles from '../campaign-stats-table-layout.module.scss';
import styles from './OrganicPostsTable.module.scss';
import {
  COLUMN_IDS,
  type ColumnId,
  COLUMN_SIZES,
  COLUMN_ID_TO_API_FIELD,
  DEFAULT_SORT,
  parseSortString,
} from './constants';
import {getEngagementRateFormula} from './engagementRateFormula';
import useColumnVisibility, {isColumnAvailable} from './use-column-visibility';

// Helper function to create properly typed Cell renderers
function createCellRenderer<T extends MRT_RowData>(
  render: (row: MRT_Row<T>) => React.ReactNode
): ({row}: {row: MRT_Row<T>}) => React.ReactNode {
  return ({row}) => render(row);
}

/** Labels for social stream type badges shown on thumbnails in the "All" tab */
const STREAM_LABELS: Record<SocialStream, string> = {
  feed: translate('Feed'),
  story: translate('Story'),
  reels: translate('Reel'),
};

interface OrganicPostsTableProps {
  testID?: string;
  posts: SocialMediaPost[];
  network?: string | null;
  socialStream: SocialStreamTab;
  totalCount?: number;
  onShareAll?: () => void;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onSort: (sort: string) => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  sort?: string;
  toolbarExtra?: React.ReactNode;
  isDateFiltered?: boolean;
  showEmv?: boolean;
  emvTooltip?: string;
}

function PostCell({
  row,
  socialStream,
  compact = false,
}: {
  row: MRT_Row<SocialMediaPost>;
  socialStream: SocialStreamTab;
  compact?: boolean;
}) {
  const post = row.original;
  const isAllTab = socialStream === 'all';

  // Use per-post socialStream when available (for "All" tab), fall back to tab-level prop
  const postStream = post.socialStream || socialStream;

  const contentUrl = useMemo(() => {
    if (!post.url) {
      return '#';
    }
    if (postStream === 'reels') {
      return post.url.replace(/\/reels?\//, '/p/');
    }
    return post.url;
  }, [post.url, postStream]);

  const thumbnailClass = compact
    ? styles.postThumbnailCompact
    : styles.postThumbnail;
  const placeholderClass = compact
    ? styles.postThumbnailPlaceholderCompact
    : styles.postThumbnailPlaceholder;

  const dateStr =
    compact && post.postedAt
      ? dayjs(post.postedAt).format('MMM DD, YYYY')
      : null;

  const thumbnailContent = post.thumbnailUrl ? (
    <img
      src={post.thumbnailUrl}
      alt="Post thumbnail"
      className={thumbnailClass}
    />
  ) : (
    <Box className={placeholderClass}>
      <ImageNotSupportedIcon className={styles.iconNotSupported} />
    </Box>
  );

  return (
    <Link
      to={contentUrl}
      target="_blank"
      rel="noopener noreferrer"
      keepBrandIdIfPresent={false}
      className={styles.postThumbnailButton}
    >
      {thumbnailContent}
      {isAllTab && post.socialStream && !compact && (
        <span className={styles.typeBadge}>
          {STREAM_LABELS[post.socialStream]}
        </span>
      )}
      {dateStr && <span className={styles.postDate}>{dateStr}</span>}
    </Link>
  );
}

function CreatorCell({
  row,
  showAvatar = true,
}: {
  row: MRT_Row<SocialMediaPost>;
  showAvatar?: boolean;
}) {
  const post = row.original;
  const {profile} = post;
  const brandId = useBrandId();
  const [, setSearchParams] = useSearchParams();
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Prefetch full profile on mount (cached with staleTime: Infinity).
  // Virtualization ensures only visible rows (~20-30) are mounted at once,
  // so this won't fire hundreds of requests simultaneously.
  const {profile: fullProfile} = useCreatorProfileQuery({
    profileId: profile?.id ?? null,
    brandId,
    enabled: !!profile?.id,
  });

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setIsHovered(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.target as Node)) return;

    if (profile?.id) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('creatorProfileId', profile.id);
        next.set(PROFILE_SOURCE_QUERY_PARAM, PROFILE_SOURCE.STATISTICS);
        return next;
      });
    }
  };

  return (
    <Box
      className={layoutStyles.creatorCell}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {showAvatar && profile?.imageUrl && (
        <img
          src={profile.imageUrl}
          alt={profile.displayName || 'Creator'}
          className={layoutStyles.creatorAvatar}
        />
      )}
      <span className={layoutStyles.creatorName}>
        {profile?.displayName || '—'}
      </span>
      {fullProfile && (
        <ProfilePopup
          isVisible={isHovered}
          profile={fullProfile}
          anchorEl={anchorEl}
          onClose={handleMouseLeave}
          getCreatorProfilePageUrlFunc={getCreatorProfilePageUrl.default}
        />
      )}
    </Box>
  );
}

function createEngagementRateCell(
  accessorKey: 'engagementRate' | 'engagementRateOrganic',
  noDataTitle: string
) {
  return function EngagementRateCellWithConfig({
    row,
  }: {
    row: MRT_Row<SocialMediaPost>;
  }) {
    const post = row.original;
    const rate = post[accessorKey];
    const isTopPerformer = post.isTopPerformerByEngagement;

    if (rate === null || rate === undefined) {
      return <EmptyDash title={noDataTitle} />;
    }

    return (
      <Box className={styles.engagementRate}>
        {isTopPerformer && (
          <Tooltip
            title={translate(
              'Top performing post! Consider boosting with paid spend.'
            )}
          >
            <StarIcon className={styles.starIcon} />
          </Tooltip>
        )}
        <span className={isTopPerformer ? styles.topPerformerBadge : undefined}>
          {formatPercentage(rate)}
        </span>
      </Box>
    );
  };
}

function HeaderWithTooltip({
  text,
  tooltip,
}: {
  text: string;
  /** Tooltip copy. Omit to reuse `text` (truncated labels). Pass `''` to hide it. */
  tooltip?: string;
}) {
  const header = (
    <Box className={styles.headerTextContainer}>
      <Headline size="xxs">{text}</Headline>
    </Box>
  );

  if (tooltip === '') {
    return header;
  }

  return (
    <Tooltip title={tooltip ?? text} placement="top" arrow describeChild>
      {header}
    </Tooltip>
  );
}

/**
 * Empty state shown when a campaign has zero posts (per Figma node 5195:76690).
 * Copy is intentionally the same across all stream tabs — the message is about
 * the campaign's posting activity as a whole, not the specific stream.
 */
function EmptyState() {
  return (
    <Box className={layoutStyles.emptyState}>
      <Box className={layoutStyles.emptyStateTextBlock}>
        <Headline size="sm" color={designSystemToken('semantic.fg.primary')}>
          {translate('Stats are marinating')}
        </Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            'Reach, engagement, top performers - all lighting up here once creators post to their channels.'
          )}
        </Body>
      </Box>
    </Box>
  );
}

function OrganicPostsTable({
  testID = 'organic-posts-table',
  posts,
  network,
  socialStream,
  totalCount = posts.length,
  onShareAll,
  isLoading,
  isFetching,
  isFetchingNextPage = false,
  hasNextPage,
  onLoadMore,
  onSort,
  onSearch,
  searchQuery,
  sort = DEFAULT_SORT,
  toolbarExtra,
  isDateFiltered = false,
  showEmv = false,
  emvTooltip,
}: OrganicPostsTableProps) {
  const engagementConfig = (
    {
      tiktok_v2: {
        accessorKey: 'engagementRateOrganic' as const,
        label: 'Organic Eng. Rate',
        noDataTitle: 'No data available for organic engagement rate',
      },
    } as const
  )[network ?? ''] ?? {
    accessorKey: 'engagementRate' as const,
    label: 'Eng. Rate',
    noDataTitle: 'No data available for engagement rate',
  };
  const engagementRateFormula = getEngagementRateFormula(network, socialStream);
  const EngagementRateRenderer = useMemo(
    () =>
      createEngagementRateCell(
        engagementConfig.accessorKey,
        engagementConfig.noDataTitle
      ),
    [engagementConfig.accessorKey, engagementConfig.noDataTitle]
  );
  const [sorting, setSorting] = useState<SortingState>(() =>
    parseSortString(sort)
  );

  // Sync sorting state when sort prop changes
  useEffect(() => {
    setSorting(parseSortString(sort));
  }, [sort]);

  const {columnVisibility, setColumnVisibility} = useColumnVisibility(
    posts,
    network,
    socialStream,
    showEmv
  );

  // Columns to hide when date-filtered — these fields have no snapshot data
  // and would misleadingly show all-time values alongside date-filtered metrics
  const DATE_FILTERED_HIDDEN_COLUMNS: ColumnId[] = [
    COLUMN_IDS.followers, // Account-level, not stored in snapshot DB
    COLUMN_IDS.videoViewsOrganic, // Always all-time from swap_matches
    COLUMN_IDS.videoViewsPaid, // Always all-time from swap_matches
    COLUMN_IDS.favoritesOrganic, // Always all-time from swap_matches
    COLUMN_IDS.favoritesPaid, // Always all-time from swap_matches
    COLUMN_IDS.emv, // Lifetime organic TTO fields are not windowed
  ];

  const effectiveColumnVisibility = isDateFiltered
    ? {
        ...columnVisibility,
        ...Object.fromEntries(
          DATE_FILTERED_HIDDEN_COLUMNS.map((col) => [col, false])
        ),
      }
    : columnVisibility;

  const handleColumnVisibilityChange = (
    columnId: ColumnId,
    visible: boolean
  ) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: visible,
    }));
  };

  // Handle sorting changes
  const handleSortingChange = (newSorting: SortingState) => {
    if (newSorting.length > 0) {
      setSorting(newSorting);
      const {id, desc} = newSorting[0];
      const columnId = id as ColumnId;
      const apiField = COLUMN_ID_TO_API_FIELD[columnId] || (columnId as string);
      onSort(`${desc ? '-' : ''}${apiField}`);
    } else {
      setSorting(parseSortString(DEFAULT_SORT));
      onSort(DEFAULT_SORT);
    }
  };

  // Column definitions
  const baseColumnDefs: Record<ColumnId, MRT_ColumnDef<SocialMediaPost>> = {
    [COLUMN_IDS.post]: {
      id: COLUMN_IDS.post,
      // Accessor is required for MRT to treat the column as sortable (show the
      // sort arrow); actual ordering is server-side via `posted_at`.
      accessorKey: 'postedAt',
      header: translate('Post'),
      Header: <HeaderWithTooltip text={translate('Post')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <PostCell row={row} socialStream={socialStream} compact />
      )),
      enableSorting: true,
      size: 152,
      grow: 0,
    },
    [COLUMN_IDS.creator]: {
      id: COLUMN_IDS.creator,
      header: translate('Creator'),
      Header: <HeaderWithTooltip text={translate('Creator')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <CreatorCell row={row} showAvatar={false} />
      )),
      enableSorting: false,
      size: 120,
      grow: 0,
    },
    [COLUMN_IDS.followers]: {
      id: COLUMN_IDS.followers,
      accessorKey: 'followerCount',
      header: translate('Followers'),
      Header: <HeaderWithTooltip text={translate('Followers')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.followerCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.followers],
      grow: 1,
    },
    [COLUMN_IDS.impressions]: {
      id: COLUMN_IDS.impressions,
      accessorKey: 'impressionCount',
      header: translate('Impressions'),
      Header: <HeaderWithTooltip text={translate('Impressions')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric
          value={row.original.impressionCount}
          format={formatNumber}
        />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.impressions],
      grow: 1,
    },
    [COLUMN_IDS.reach]: {
      id: COLUMN_IDS.reach,
      accessorKey: 'reachCount',
      header: translate('Reach'),
      Header: <HeaderWithTooltip text={translate('Reach')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.reachCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.reach],
      grow: 1,
    },
    [COLUMN_IDS.comments]: {
      id: COLUMN_IDS.comments,
      accessorKey: 'commentCount',
      header: translate('Comments'),
      Header: <HeaderWithTooltip text={translate('Comments')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.commentCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.comments],
      grow: 1,
    },
    [COLUMN_IDS.likes]: {
      id: COLUMN_IDS.likes,
      accessorKey: 'likeCount',
      header: translate('Likes'),
      Header: <HeaderWithTooltip text={translate('Likes')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.likeCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.likes],
      grow: 1,
    },
    [COLUMN_IDS.saves]: {
      id: COLUMN_IDS.saves,
      accessorKey: 'savedCount',
      header: translate('Saves'),
      Header: <HeaderWithTooltip text={translate('Saves')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.savedCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.saves],
      grow: 1,
    },
    [COLUMN_IDS.views]: {
      id: COLUMN_IDS.views,
      accessorKey: 'viewCount',
      header: translate('Views'),
      Header: <HeaderWithTooltip text={translate('Views')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.viewCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.views],
      grow: 1,
    },
    [COLUMN_IDS.avgWatchTime]: {
      id: COLUMN_IDS.avgWatchTime,
      accessorKey: 'videoAverageWatchTime',
      header: translate('Avg Watch Time'),
      Header: <HeaderWithTooltip text={translate('Avg Watch Time')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric
          value={row.original.videoAverageWatchTime}
          format={formatDuration}
        />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.avgWatchTime],
      grow: 1,
    },
    [COLUMN_IDS.videoViewsOrganic]: {
      id: COLUMN_IDS.videoViewsOrganic,
      accessorKey: 'videoViewsOrganic',
      header: translate('Organic Views'),
      Header: <HeaderWithTooltip text={translate('Organic Views')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric
          value={row.original.videoViewsOrganic}
          format={formatNumber}
        />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.videoViewsOrganic],
      grow: 1,
    },
    [COLUMN_IDS.videoViewsPaid]: {
      id: COLUMN_IDS.videoViewsPaid,
      accessorKey: 'videoViewsPaid',
      header: translate('Paid Views'),
      Header: <HeaderWithTooltip text={translate('Paid Views')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric
          value={row.original.videoViewsPaid}
          format={formatNumber}
        />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.videoViewsPaid],
      grow: 1,
    },
    [COLUMN_IDS.favoritesOrganic]: {
      id: COLUMN_IDS.favoritesOrganic,
      accessorKey: 'favoritesOrganic',
      header: translate('Organic Saves'),
      Header: <HeaderWithTooltip text={translate('Organic Saves')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric
          value={row.original.favoritesOrganic}
          format={formatNumber}
        />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.favoritesOrganic],
      grow: 1,
    },
    [COLUMN_IDS.favoritesPaid]: {
      id: COLUMN_IDS.favoritesPaid,
      accessorKey: 'favoritesPaid',
      header: translate('Paid Saves'),
      Header: <HeaderWithTooltip text={translate('Paid Saves')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.favoritesPaid} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.favoritesPaid],
      grow: 1,
    },
    [COLUMN_IDS.shares]: {
      id: COLUMN_IDS.shares,
      accessorKey: 'shareCount',
      header: translate('Shares'),
      Header: <HeaderWithTooltip text={translate('Shares')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric value={row.original.shareCount} format={formatNumber} />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.shares],
      grow: 1,
    },
    [COLUMN_IDS.totalInteractions]: {
      id: COLUMN_IDS.totalInteractions,
      accessorKey: 'totalInteractionsCount',
      header: translate('Total Interactions'),
      Header: <HeaderWithTooltip text={translate('Total Interactions')} />,
      Cell: createCellRenderer<SocialMediaPost>((row) => (
        <EmptyMetric
          value={row.original.totalInteractionsCount}
          format={formatNumber}
        />
      )),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.totalInteractions],
      grow: 1,
    },
    [COLUMN_IDS.engagementRate]: {
      id: COLUMN_IDS.engagementRate,
      accessorKey: engagementConfig.accessorKey,
      header: translate(engagementConfig.label),
      Header: (
        <HeaderWithTooltip
          text={translate(engagementConfig.label)}
          tooltip={
            engagementRateFormula ? translate(engagementRateFormula) : ''
          }
        />
      ),
      Cell: EngagementRateRenderer,
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.engagementRate],
      grow: 0,
    },
    [COLUMN_IDS.emv]: {
      id: COLUMN_IDS.emv,
      accessorKey: 'emv',
      header: translate('EMV'),
      Header: (
        <HeaderWithTooltip text={translate('EMV')} tooltip={emvTooltip ?? ''} />
      ),
      Cell: createCellRenderer<SocialMediaPost>((row) =>
        formatCurrency(row.original.emv)
      ),
      enableSorting: false,
      size: COLUMN_SIZES[COLUMN_IDS.emv],
      grow: 1,
    },
  };

  const columnOrder = Object.values(COLUMN_IDS).filter(
    (colId) => showEmv || colId !== COLUMN_IDS.emv
  );

  const columns: MRT_ColumnDef<SocialMediaPost>[] = columnOrder.map(
    (colId) => ({
      ...baseColumnDefs[colId],
      muiTableHeadCellProps: {align: 'left' as const},
      muiTableBodyCellProps: {align: 'left' as const},
    })
  );

  // Column configs for the selector — hide followers when date-filtered
  const allColumnConfigs: ColumnDefinition<ColumnId>[] = [
    {id: COLUMN_IDS.post, label: translate('Post')},
    {id: COLUMN_IDS.creator, label: translate('Creator')},
    {id: COLUMN_IDS.followers, label: translate('Followers')},
    {id: COLUMN_IDS.impressions, label: translate('Impressions')},
    {id: COLUMN_IDS.reach, label: translate('Reach')},
    {id: COLUMN_IDS.comments, label: translate('Comments')},
    {id: COLUMN_IDS.likes, label: translate('Likes')},
    {id: COLUMN_IDS.saves, label: translate('Saves')},
    {id: COLUMN_IDS.views, label: translate('Views')},
    {id: COLUMN_IDS.avgWatchTime, label: translate('Avg Watch Time')},
    {id: COLUMN_IDS.videoViewsOrganic, label: translate('Organic Views')},
    {id: COLUMN_IDS.videoViewsPaid, label: translate('Paid Views')},
    {id: COLUMN_IDS.favoritesOrganic, label: translate('Organic Saves')},
    {id: COLUMN_IDS.favoritesPaid, label: translate('Paid Saves')},
    {id: COLUMN_IDS.shares, label: translate('Shares')},
    {id: COLUMN_IDS.totalInteractions, label: translate('Total Interactions')},
    {id: COLUMN_IDS.engagementRate, label: translate(engagementConfig.label)},
    {id: COLUMN_IDS.emv, label: translate('EMV')},
  ];
  // Only offer columns the network/stream supports, so a column hidden by
  // availability (e.g. TikTok Impressions) can't be toggled back on.
  const columnConfigs = allColumnConfigs.filter(
    (col) =>
      isColumnAvailable(col.id, network, socialStream) &&
      !(isDateFiltered && DATE_FILTERED_HIDDEN_COLUMNS.includes(col.id)) &&
      (showEmv || col.id !== COLUMN_IDS.emv)
  );

  // Handle infinite scroll — guard on isFetchingNextPage only so background
  // refetches don't block loading the next page.
  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      onLoadMore();
    }
  };

  return (
    <Box className={layoutStyles.container} data-testid={testID}>
      <Stack direction="row" className={layoutStyles.toolbar}>
        <Box className={layoutStyles.searchContainer}>
          <SearchInput
            value={searchQuery}
            onChange={onSearch}
            onSubmit={onSearch}
            placeholder={translate('Search by creator name...')}
          />
        </Box>
        <Box className={layoutStyles.toolbarActions}>
          <Box className={layoutStyles.resultsCount}>
            {translate('Showing {count}', {
              count: totalCount,
            })}
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            className={layoutStyles.toolbarSeparator}
          />
          <Button
            size="small"
            appearance="neutral"
            mode="plain"
            onClick={onShareAll}
          >
            <Icon name="Actions-Share" size="small" appearance="neutral" />
            {translate('Share All')}
          </Button>
          {toolbarExtra}
          <ColumnSelector
            testID={`${testID}-column-selector`}
            columns={columnConfigs}
            visibleColumns={effectiveColumnVisibility}
            canPinColumns={false}
            pinnedColumns={[]}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onColumnPinChange={() => {}}
          />
        </Box>
      </Stack>
      <Box
        className={clsx(
          layoutStyles.tableCard,
          !isLoading && posts.length === 0 && layoutStyles.tableCardEmpty
        )}
      >
        <Table
          testID={`${testID}-table`}
          data={posts}
          columns={columns}
          columnOrder={columnOrder}
          isLoadingInitial={isLoading}
          isLoading={isFetching}
          showProgressBars={isFetching}
          visibleColumns={effectiveColumnVisibility}
          rowSelection={{}}
          sorting={sorting}
          onSorting={handleSortingChange}
          enableVirtualization
          enableSorting
          manualSorting
          onEndReached={handleEndReached}
          tableScrollTopKey="organicPostsTableScrollTop"
          emptyStateComponent={null}
        />
        {!isLoading && posts.length === 0 ? <EmptyState /> : null}
        {isFetchingNextPage && (
          <Box className={styles.loadingMore}>
            <CircularProgress size={24} />
            <span>{translate('Loading more posts...')}</span>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default OrganicPostsTable;
